import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';

import { api } from '@/api';
import { OrganizationTypes } from '@/common/constants';
import type { IOrgBalanceResponse,IOrgBalancesPageResponse } from '@/dtos';
import { API_URL } from '@/dtos/generated-paths';

import { BankingBalanceItem } from '../types';

interface Props {
  parentOrgId?: string;
  statuses: string[];
  page: number;
  size: number;
}

const QUERY_KEY = 'useBankingBalancesQuery';

const mapOrgBalanceResponseToItem = (dto: IOrgBalanceResponse): BankingBalanceItem => ({
  orgId: dto?.org_id,
  orgName: dto?.org_name,
  orgType: dto?.org_type as unknown as OrganizationTypes,
  hsaBalance: dto.hsa_balance ?? 0,
  notionalBalance: dto.notional_balance ?? 0,
  status: dto.status || 'Unknown',
  isLocked: dto.is_locked ?? false,
  logoUrl: dto.logo_info?.dark_large_logo_url || dto.logo_info?.large_logo_url,
});

export const useBankingBalancesQuery = ({ parentOrgId, statuses, page, size }: Props) => {
  const { data, isLoading, refetch } = useQuery(
    [QUERY_KEY, parentOrgId, statuses, page, size],
    () => {
      return api.get<IOrgBalancesPageResponse>(
        API_URL.OMNI_BALANCES_ORGANIZATIONS_CHILDREN_BY_PARENT_ORG_ID(parentOrgId!),
        {
          params: {
            statuses: statuses.length ? statuses.join(',') : undefined,
            page,
            size,
          },
        }
      );
    },
    {
      enabled: Boolean(parentOrgId),
    }
  );

  const formattedList = useMemo(() => {
    const rawContent = data?.data?.content || [];
    return rawContent.map(mapOrgBalanceResponseToItem) || [];
  }, [data]);

  return {
    data: formattedList,
    total: data?.data?.total_elements || formattedList.length || 0,
    isLoading,
    refetch,
  };
};

export default useBankingBalancesQuery;
